/**
 * ProgressIndicator
 *
 * @package ZanduraUI
 * @author Falk Hermann <falk.hermann@zandura.net>
 * @author Johannes Pommranz <johannes.pommranz@zandura.net>
 */

import uuid from 'uuid/v4';
import zuiDispatchEvent from '../helpers/dispatch-event';
import zuiIdentify from '../helpers/identify';

/**
 * ProgressIndicator
 * =================
 *
 * Methods
 * -------
 *
 * reset() => reset any timer / value / progress
 *
 * show() => display the element
 *
 * hide() => hide the element
 *
 * setValue(<percent>) => set absolute value in percent
 *
 * startProcess(<id>, <progress>) => start a new process, <id> is optional and will be auto-generated if missing
 *
 * setProcessProgress(<id>, <progress>) => set progress of an process
 *
 * finishProcess(<id>) => finish a process ( setProgress(id, 100) )
 *
 * finishAllProcesses() => finish all processes
 *
 */
export default class ProgressIndicator {

  /**
   * @param {HTMLElement }element
   */
  constructor(element) {
    this.$el = element;
    this.$stack = []; // stack of running items
    this.$fadeTimeout = 600;
    this.$runningEffect = null;

    this.$svg = null;
    this.$primaryBar = null;
    this.$secondaryBar = null;
    this.$circle = null;

    this.$timerInterval = 0;

    this.init();

    this.$el.classList.add('updated');
  }

  init() {
    /*  insert bars for linear animation  */
    if (this.isLinear()) {
      this.$primaryBar = document.querySelector(`#${zuiIdentify(this.$el)} .primary-bar`);
      if (!this.$primaryBar) {
        this.$primaryBar = document.createElement('div');
        this.$primaryBar.classList.add('primary-bar');
        this.$el.appendChild(this.$primaryBar);
      }
      this.$secondaryBar = document.querySelector(`#${zuiIdentify(this.$el)} .secondary-bar`);
      if (!this.$secondaryBar) {
        this.$secondaryBar = document.createElement('div');
        this.$secondaryBar.classList.add('secondary-bar');
        this.$el.appendChild(this.$secondaryBar);
      }
    } else
    /*  insert svg for circular animation  */
    if (this.isCircular() && !document.querySelector(`#${zuiIdentify(this.$el)} svg`)) {
      this.$circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      this.$circle.setAttribute('cx', '24');
      this.$circle.setAttribute('cy', '24');
      this.$circle.setAttribute('r', '20');

      this.$svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      this.$svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      this.$svg.setAttribute('viewBox', '0 0 48 48');
      this.$svg.appendChild(this.$circle);
      this.$el.appendChild(this.$svg);
    }

    if (this.isDeterminate()) {
      if (this.$el.hasAttribute('data-zui-timing')) {
        this.startTimer(this.$el.getAttribute('data-zui-timing'));
      } else {
        this.setValue(parseFloat(this.$el.getAttribute('aria-valuenow')));
      }
    }
  }

  isDeterminate() {
    return this.$el.hasAttribute('aria-valuenow');
  }

  isLinear() {
    return this.$el.classList.contains('linear');
  }

  isCircular() {
    return this.$el.classList.contains('circular');
  }

  reset() {
    this.hide();
    if (this.$timerInterval) {
      clearInterval(this.$timerInterval);
    }
    this.$stack.splice(0, this.$stack.length);
    if (this.isDeterminate()) {
      this.$el.setAttribute('aria-valuenow', '0');
    }
  }

  /**
   * show / fade in
   * stop running fade if necessary
   */
  show() {
    if (this.$runningEffect) {
      this.$el.classList.remove('fade-in', 'fade-out');
      clearTimeout(this.$runningEffect);
    }

    this.$el.setAttribute('aria-hidden', 'false');
    this.$el.classList.add('fade-in');

    this.$runningEffect = setTimeout(() => {
      this.$el.classList.remove('fade-in');
    }, this.$fadeTimeout);
  }

  /**
   * hide / fade out
   * stop running fade if necessary
   */
  hide() {
    if (this.$runningEffect) {
      this.$el.classList.remove('fade-in', 'fade-out');
      clearTimeout(this.$runningEffect);
    }

    this.$el.classList.add('fade-out');

    this.$runningEffect = setTimeout(() => {
      this.$el.setAttribute('aria-hidden', 'true');
      this.$el.classList.remove('fade-out');
    }, this.$fadeTimeout);
  }

  /**
   * set the direct value in percent
   *
   * @param {number} value
   */
  setValue(value = undefined) {
    if (value === undefined || value === null) {

      // make indeterminate
      this.$el.removeAttribute('aria-valuenow');

      // remove timer
      if (this.$timerInterval) {
        clearInterval(this.$timerInterval);
      }
      if (this.$el.getAttribute('data-zui-timing')) {
        this.$el.removeAttribute('data-zui-timing');
      }

      // reset styles
      if (this.isLinear()) {
        this.$primaryBar.style.removeProperty('transform');
      } else if (this.isCircular()) {
        this.$circle.style.removeProperty('stroke-dashoffset');
        this.$circle.style.removeProperty('stroke-dasharray');
      }
      return 0;
    }

    // force 0 < val < 100
    const val = Math.min(Math.max(0, parseInt(`${value}`, 10)), 100);

    this.$el.setAttribute('aria-valuenow', `${val}`);

    // linear
    if (this.isLinear()) {
      this.$primaryBar.style.transform = `scaleX(${(val / 100).toFixed(2)})`;
    } else
    // circular
    if (this.isCircular()) {
      // linear
      const radius = parseInt(this.$circle.getAttribute('r'), 10);
      const circumference = 2 * Math.PI * radius;
      const diff = ((100 - val) / 100);
      const offset = diff * circumference;
      this.$circle.style.strokeDasharray = `${circumference}px`;
      this.$circle.style.strokeDashoffset = `${offset}px`;
    }

    // empty $stack if all finished
    if (val === 100) {
      this.$stack.splice(0, this.$stack.length);
      zuiDispatchEvent(this.$el, 'zui:progress-indicator:all-finished', {
        $el: this.$el,
      });
    }

    return val;
  }

  /**
   * @param {string} id
   * @param {number} progress
   * @return {string}
   */
  startProcess(id = undefined, progress = undefined) {

    const item = {
      id: id || uuid(),
      progress: progress || 0,
    };

    // check if item/id already exists
    if (!this.$stack.find(itm => itm.id === item.id)) {
      this.$stack.push(item);
    }

    this.setProcessProgress(item.id, item.progress);

    this.show();

    return item.id;
  }

  /**
   * @param {string} id
   * @param {number} progress
   * @return {number}
   */
  setProcessProgress(id, progress) {

    const item = this.$stack.find(itm => itm.id === id);

    if (item) {
      // remove timer, if running
      if (this.$timerInterval) {
        clearInterval(this.$timerInterval);
      }
      if (this.$el.getAttribute('data-zui-timing')) {
        this.$el.removeAttribute('data-zui-timing');
      }

      item.progress = progress;
    }

    // calculate, set and return average value
    return this.setValue(
      parseFloat(
        (this.$stack.reduce((total, current) => total + current.progress, 0) / this.$stack.length)
          .toFixed(2)
      )
    );
  }

  /**
   * @param {string} id
   * @return {number}
   */
  finishProcess(id) {
    return this.setProcessProgress(id, 100);
  }

  finishAllProcesses() {
    this.$stack.forEach(itm => this.setProcessProgress(itm.id, 100));
  }

  /**
   * @param {number|string} value
   */
  startTimer(value = undefined) {

    const val = parseInt(`${value || this.$el.getAttribute('data-zui-timing')}`, 10);
    const fps = Math.round(1000 / 24); // 24 images per second // https://de.wikipedia.org/wiki/Bildfrequenz
    const start = Date.now();
    const end = start + val;

    if (this.$timerInterval) {
      clearInterval(this.$timerInterval);
    }

    this.setValue(0);
    this.show();

    this.$timerInterval = setInterval(() => {
      const now = Date.now();
      let fraction = parseFloat(((now - start) / (end - start)).toFixed(4));
      let rotate = 0;

      if (fraction >= 1) {
        // finish
        fraction = 1;
        clearInterval(this.$timerInterval);
      }

      // update progress-indicator

      // ease in quadratic / decelerate // @see https://gist.github.com/gre/1650294
      fraction *= (2 - fraction);

      if (this.isCircular()) {
        // rotate the entire element (only the first 70% of the animation)
        rotate = Math.min(Math.max(0, fraction / 0.70), 1);
        // ease out cubic / decelerate // @see https://gist.github.com/gre/1650294
        rotate = (rotate -= 1) * rotate * rotate + 1;
        this.$el.style.transform = `rotate(${Math.round(rotate * 270)}deg)`;
      }

      this.setValue(fraction * 100);
    }, fps);
  }
}

/**
 * @param {HTMLElement} element
 * @param {ProgressIndicator} element.zuiProgressIndicator
 * @constructor
 * @return ProgressIndicator;
 */
export function ProgressIndicatorFactory(element) {
  if (element.zuiProgressIndicator === undefined) {
    Object.defineProperty(element, 'zuiProgressIndicator', {
      enumerable: false,
      writable: false,
      value: new ProgressIndicator(element),
    });
  }
  return element.zuiProgressIndicator;
}
